import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Send, User, Coins, Mail, MoreVertical, Ban, Flag, ImagePlus, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { optimizeImg } from "@/lib/img";
import { useAuth } from "@/context/AuthContext";
import BottomNav from "@/components/BottomNav";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function Messages() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const to = params.get("to");
  const convParam = params.get("c");

  const [convId, setConvId] = useState(convParam || null);
  const [list, setList] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [pendingCharge, setPendingCharge] = useState(0);
  const [sending, setSending] = useState(false);
  const [menu, setMenu] = useState(false);
  const [pendingImg, setPendingImg] = useState(null);
  const [imgBusy, setImgBusy] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);

  const inThread = !!(convId || to);

  const loadList = useCallback(async () => {
    try { const { data } = await api.get("/conversations"); setList(data.conversations); }
    catch { setList([]); }
  }, []);

  // resolve `to` into a conversation
  useEffect(() => {
    if (!to) return;
    api.post("/conversations", { to }).then(({ data }) => {
      setConvId(data.id); setPendingCharge(data.pending_charge || 0);
      params.delete("to"); params.set("c", data.id); setParams(params, { replace: true });
    }).catch((e) => { toast.error(e.response?.data?.detail || "Couldn't open chat"); navigate("/messages"); });
    // eslint-disable-next-line
  }, [to]);

  const loadThread = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/conversations/${id}/messages`);
      setThread(data);
      if (typeof data.pending_charge === "number") setPendingCharge(data.pending_charge);
    } catch { toast.error("Chat not found"); navigate("/messages"); }
  }, [navigate]);

  useEffect(() => { if (!inThread) loadList(); }, [inThread, loadList]);
  useEffect(() => { if (convId) loadThread(convId); }, [convId, loadThread]);

  // poll open thread
  useEffect(() => {
    if (!convId) return;
    const t = setInterval(() => loadThread(convId), 5000);
    return () => clearInterval(t);
  }, [convId, loadThread]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread?.messages?.length]);

  const send = async () => {
    const body = text.trim();
    if ((!body && !pendingImg) || !convId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${convId}/messages`, { text: body, image_url: pendingImg });
      setText(""); setPendingImg(null); setPendingCharge(0);
      if (data.charged > 0) { toast.success(`${data.charged} coins sent to creator`); refreshUser(); }
      setThread((t) => ({ ...t, messages: [...(t?.messages || []), data.message] }));
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send"); }
    finally { setSending(false); }
  };

  const attachImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setImgBusy(true);
    try {
      const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
      const { data } = await api.post("/uploads/image", { image_base64: b64, origin_url: window.location.origin });
      setPendingImg(data.url);
    } catch { toast.error("Upload failed"); } finally { setImgBusy(false); e.target.value = ""; }
  };

  const blockUser = async () => {
    setMenu(false);
    if (!thread?.other) return;
    try {
      const { data } = await api.post(`/users/${thread.other.id}/block`);
      toast.success(data.blocked ? "User blocked" : "User unblocked");
      loadThread(convId);
    } catch { toast.error("Couldn't update block"); }
  };
  const reportUser = async () => {
    setMenu(false);
    const reason = window.prompt("Report this conversation — what's wrong?");
    if (reason === null) return;
    try {
      await api.post("/reports", { target_id: thread?.other?.id, conversation_id: convId, reason, context: "message" });
      toast.success("Reported — thanks. Our team will review it.");
    } catch { toast.error("Report failed"); }
  };

  // ---------- THREAD VIEW ----------
  if (inThread) {
    const other = thread?.other;
    const iBlocked = thread?.i_blocked;
    const blocked = thread?.blocked;
    return (
      <div className="min-h-screen bg-ink-950 text-[#F0E9DD] flex flex-col">
        <header className="sticky top-0 z-30 bg-ink-950/90 backdrop-blur-xl border-b border-white/10 px-3 h-14 flex items-center gap-3">
          <button onClick={() => navigate("/messages")} data-testid="thread-back" className="text-zinc-400"><ArrowLeft size={22} /></button>
          {other && (
            <button onClick={() => navigate(`/u/${other.id}`)} className="flex items-center gap-2 min-w-0 flex-1">
              <div className="w-8 h-8 rounded-full overflow-hidden bg-ink-800 shrink-0">
                {other.avatar_url ? <img src={optimizeImg(other.avatar_url, 80, 70)} alt="" className="w-full h-full object-cover" /> : <User size={15} className="m-auto mt-2 text-zinc-500" />}</div>
              <span className="font-heading font-bold truncate" data-testid="thread-name">{other.username || other.name}</span>
            </button>
          )}
          <div className="relative">
            <button onClick={() => setMenu((v) => !v)} data-testid="thread-menu" className="text-zinc-400 hover:text-white p-1"><MoreVertical size={20} /></button>
            {menu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
                <div className="absolute right-0 top-9 z-20 w-44 bg-ink-800 border border-zinc-700 rounded-xl overflow-hidden shadow-2xl" data-testid="thread-menu-panel">
                  <button onClick={blockUser} data-testid="thread-block" className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-ink-700 text-left">{iBlocked ? <><Ban size={15} /> Unblock</> : <><Ban size={15} /> Block</>}</button>
                  <button onClick={reportUser} data-testid="thread-report" className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-ink-700 text-left text-red-400"><Flag size={15} /> Report</button>
                </div>
              </>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto px-3 py-4 space-y-2 pb-4" data-testid="thread-messages">
          {!thread ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gold" size={24} /></div>
            : thread.messages.length === 0 ? <p className="text-center text-zinc-600 text-sm py-10">Say hello 👋</p>
            : thread.messages.map((m) => (
              <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[78%] rounded-2xl px-1 py-1 text-sm ${m.mine ? "bg-gold text-ink-950 rounded-br-sm" : "bg-ink-800 text-[#F0E9DD] rounded-bl-sm"}`}>
                  {m.image_url && <img src={m.image_url} alt="" className="rounded-xl max-h-64 w-full object-cover mb-1" data-testid="message-image" />}
                  {m.text && <div className="px-2.5 py-1">{m.text}</div>}
                  <div className={`text-[9px] px-2.5 pb-0.5 ${m.mine ? "text-ink-950/60" : "text-zinc-500"}`}>{timeAgo(m.created_at)}</div>
                </div>
              </div>
            ))}
          <div ref={endRef} />
        </main>

        {pendingCharge > 0 && !blocked && (
          <div className="px-3 py-2 bg-[#1f1808] border-t border-yellow-500/30 flex items-center gap-2 text-xs text-yellow-200" data-testid="dm-charge-notice">
            <Coins size={14} /> Messaging {thread?.other?.username || "this creator"} costs {pendingCharge} coins (charged once).
          </div>
        )}
        {blocked ? (
          <div className="sticky bottom-0 bg-ink-950/95 border-t border-white/10 px-3 py-4 text-center text-sm text-zinc-500" data-testid="thread-blocked-notice">
            {iBlocked ? "You've blocked this user. Unblock from the menu to message again." : "You can't reply to this conversation."}
          </div>
        ) : (
          <div className="sticky bottom-0 bg-ink-950/95 backdrop-blur-xl border-t border-white/10 px-3 py-2.5">
            {pendingImg && (
              <div className="relative inline-block mb-2" data-testid="pending-image">
                <img src={pendingImg} alt="" className="h-20 rounded-lg object-cover" />
                <button onClick={() => setPendingImg(null)} className="absolute -top-2 -right-2 bg-ink-800 border border-zinc-700 rounded-full p-0.5"><X size={13} /></button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept="image/*" onChange={attachImage} className="hidden" data-testid="message-image-input" />
              <button onClick={() => fileRef.current?.click()} disabled={imgBusy} data-testid="message-attach" className="w-10 h-10 rounded-full bg-ink-900 border border-zinc-800 text-zinc-300 flex items-center justify-center shrink-0 disabled:opacity-50">
                {imgBusy ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={18} />}</button>
              <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Message…" data-testid="message-input"
                className="flex-1 bg-ink-900 border border-zinc-800 rounded-full px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
              <button onClick={send} disabled={sending || (!text.trim() && !pendingImg)} data-testid="message-send"
                className="w-10 h-10 rounded-full bg-gold text-ink-950 flex items-center justify-center disabled:opacity-40 shrink-0">
                {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---------- INBOX LIST ----------
  return (
    <div className="min-h-screen bg-ink-950 text-[#F0E9DD] pb-24">
      <header className="sticky top-0 z-30 bg-ink-950/90 backdrop-blur-xl border-b border-white/10 px-4 h-14 flex items-center gap-3">
        <button onClick={() => navigate("/notifications")} className="text-zinc-400"><ArrowLeft size={22} /></button>
        <h1 className="font-heading font-black uppercase tracking-tight text-xl">Messages</h1>
      </header>
      <main className="max-w-xl mx-auto px-3 pt-3">
        {list === null ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gold" size={24} /></div>
          : list.length === 0 ? (
            <div className="border border-dashed border-zinc-800 rounded-xl py-20 flex flex-col items-center text-center" data-testid="messages-empty">
              <Mail size={34} className="text-zinc-700 mb-3" /><p className="text-zinc-400">No messages yet.</p>
              <p className="text-xs text-zinc-600 mt-1">Tap Message on any profile to start a chat.</p>
            </div>
          ) : (
            <div className="space-y-1" data-testid="conversations-list">
              {list.map((c) => (
                <button key={c.id} onClick={() => { setConvId(c.id); setParams({ c: c.id }); }} data-testid={`conv-${c.other.id}`}
                  className="w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left hover:bg-ink-900">
                  <div className="w-11 h-11 rounded-full overflow-hidden bg-ink-800 shrink-0">
                    {c.other.avatar_url ? <img src={optimizeImg(c.other.avatar_url, 96, 70)} alt="" className="w-full h-full object-cover" /> : <User size={18} className="m-auto mt-3 text-zinc-500" />}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2"><span className="font-heading font-bold truncate">{c.other.username || c.other.name}</span>
                      {c.updated_at && <span className="text-[10px] text-zinc-600 ml-auto shrink-0">{timeAgo(c.updated_at)}</span>}</div>
                    <div className="text-sm text-zinc-500 truncate">{c.last_message || "New conversation"}</div>
                  </div>
                  {c.unread > 0 && <span className="w-5 h-5 rounded-full bg-gold text-ink-950 text-[10px] font-bold flex items-center justify-center shrink-0" data-testid={`conv-unread-${c.other.id}`}>{c.unread}</span>}
                </button>
              ))}
            </div>
          )}
      </main>
      <BottomNav />
    </div>
  );
}
